import * as React from 'react';
import {
    DefineTagOptions,
    DeviceType,
    Keywords,
    NativeAdObj,
    PageType,
    Status,
} from '../../../../shared/types/ast';
import { publish } from '../../../podlet/events';
import { ErrorBoundary } from '../../error-boundary';
import { displayAdsLog } from '../../../utils/debug';
import { RecircNativeAd } from '../../native/recirc-native';
import { loadDivInDocumentAndRemove } from '../utils';
import { useState } from 'react';
import { warnIfRemoved } from '../../../hooks/native';
import { inscreen, lazyLoad } from '../../../utils/lazy';
import { isNative } from '../../../utils/ast';
import { markAdAvailable, markTagLoaded } from '../../../utils/performance';
import { getConfig } from '../../../config';
import {
    defineTag,
    loadAstTags,
    onAdAvailable,
    onAdBadRequest,
    onAdLoaded,
    onAdNoBid,
    setPageOpts,
} from '../../../podlet/ast';
import { setupPageOptsAndGetXandrData } from '../../../utils/page-opts';

function recircIndex(id: string) {
    const lastNumber = /(\d+)(?!.*\d)/;
    const numberAsMatch = id.match(lastNumber);
    if (numberAsMatch) {
        const numberAsString = numberAsMatch[0];
        const parsedNumber = parseInt(numberAsString, 10);
        return parsedNumber > 3 ? 4 : parsedNumber;
    }
    return 0;
}
const bannerDeviceType = (deviceType: DeviceType) => {
    switch (deviceType) {
        case 'desktop':
            return 'wde';
        case 'tablet':
            return 'wtb';
        case 'mobile':
            return 'wph';
    }
};

export type Props = {
    instanceId: string;
    pageType?: PageType;
    deviceType?: DeviceType;
    keywords: Keywords;
};

const RecircNativeAdReact = ({ instanceId, keywords }: Props) => {
    const { pageType, deviceType } = getConfig();
    const containerRef = React.useRef<HTMLDivElement>(null);

    const [loaded, setLoaded] = useState(false);
    const [data, setData] = useState<NativeAdObj>();
    const [status, setStatus] = useState<Status>('requested');

    const [hasRun, setHasRun] = useState(false);
    const [inProximity, setInProximity] = useState(false);

    React.useEffect(() => {
        const containerElem = containerRef.current;
        const pageOpts = setupPageOptsAndGetXandrData();

        // TODO: Move this to DB config
        function getCustomTag(): DefineTagOptions {
            displayAdsLog(`Inside RecircNativeAdReact getCustomTag `);
            const index = recircIndex(instanceId);
            const devicePrefix = bannerDeviceType(deviceType);
            const formatPrefix = deviceType === 'mobile' ? 'board' : 'netboard';
            const categoryKeywords = Object.entries(keywords).reduce(
                (previousValue, [key, value]) => {
                    // @ts-ignore
                    previousValue[`no-sno-finn-${key}`] = value;
                    return previousValue;
                },
                {} as Keywords,
            );
            return {
                invCode: `no-finn-${devicePrefix}-${pageType}_recirc_${index}`,
                sizes: [[1, 1]],
                allowedFormats: ['native'],
                targetId: instanceId,
                keywords: {
                    'no-sno-finn-section': 'bap',
                    'no-sno-finn-subsection': 'bap_forsale',
                    'no-sno-adformat': [`${formatPrefix}_recirc`],
                    ...categoryKeywords,
                },
                disablePsa: true,
                member: pageOpts?.member || 9700,
            };
        }

        if (
            inProximity &&
            !hasRun &&
            containerElem &&
            pageType &&
            deviceType &&
            window.apntag &&
            window.apntag.anq
        ) {
            setHasRun(true);
            window.apntag.anq.push(() => {
                displayAdsLog(`setup streamtag`);
                const streamTag = getCustomTag();
                if (window.apntag) {
                    displayAdsLog(`defineTag: ${JSON.stringify(streamTag)}`);
                    setPageOpts(pageOpts);

                    loadDivInDocumentAndRemove(instanceId, () => {
                        displayAdsLog(
                            'debouncedLoadTagsSafe FROM recirc-native-ad.react.tsx',
                        );
                        defineTag(streamTag);
                        loadAstTags();
                    });

                    onAdAvailable(instanceId, (adObj) => {
                        markAdAvailable(instanceId);
                        if (isNative(adObj)) {
                            displayAdsLog(`recirc L125`);
                            setData(adObj);
                            displayAdsLog(`handleInScreenTrack`);

                            displayAdsLog(`recirc L131`);
                            if (containerElem && window.apntag) {
                                const cleanup = warnIfRemoved(containerElem);
                                inscreen(containerElem, () => {
                                    displayAdsLog(`inscreen`);
                                    cleanup();
                                    window.apntag.anq.push(() => {
                                        window.apntag.showTag(adObj.targetId);
                                    });
                                });
                            }
                        }
                        setLoaded(true);
                        setStatus('available');
                    });

                    onAdLoaded(instanceId, (adObj) => {
                        markTagLoaded(instanceId);
                        publish('recirc-native-ad', 'in-view', true);

                        window.apntag.fireImpressionTrackers(
                            adObj as NativeAdObj,
                        );
                        // This is done with the click event handler
                        // window.apntag.attachClickTrackers(
                        //     data,
                        //     data.targetId,
                        // );
                    });

                    onAdNoBid(instanceId, () => {
                        setLoaded(true);
                        setStatus('loaded');
                        publish('recirc-native-ad', 'load-failed', true);
                    });

                    onAdBadRequest(instanceId, () => {
                        setStatus('failed');
                        setLoaded(true);
                        publish('recirc-native-ad', 'load-failed', true);
                    });
                }
            });
        } else {
            setLoaded(true);
        }
    }, [data, deviceType, pageType, hasRun, instanceId, keywords, inProximity]);

    React.useEffect(() => {
        const containerElem = containerRef.current;
        if (!inProximity && containerElem) {
            const cleanup = warnIfRemoved(containerElem);
            lazyLoad(
                containerElem,
                () => {
                    displayAdsLog(`inscreen`);
                    cleanup();
                    setInProximity(true);
                },
                {
                    proximity: 100,
                },
            );
        }
    }, [inProximity, containerRef]);

    // Publish click event back to host podlet (data-recom/mlt)
    React.useEffect(() => {
        const elem = containerRef.current;
        displayAdsLog(`attach click event handler: ${status} ${elem}`);

        const handleClickTracker = (e) => {
            displayAdsLog('handleClickTracker()');
            e.stopPropagation();
            if (data) {
                loadDivInDocumentAndRemove(data.targetId, (element) => {
                    displayAdsLog(
                        `loadDivInDocumentAndRemove attachClickTrackers ${data.targetId} `,
                    );
                    window.apntag.attachClickTrackers(data, data.targetId);
                    element.click();
                });
            }

            publish('recirc-native-ad', 'external-click', true);
        };
        if (elem) {
            elem.addEventListener('click', handleClickTracker);
        }
        return () => {
            if (elem) {
                elem.removeEventListener('click', handleClickTracker);
            }
        };
    }, [containerRef, data, status]);

    function renderAd() {
        if (!data) return null;
        else
            return (
                <RecircNativeAd
                    adObj={data as NativeAdObj}
                    status={status}
                    id={instanceId}
                    grid={true}
                    deviceType={deviceType}
                    className={' '}
                />
            );
    }

    if (loaded && status === 'failed') return null;
    return <div ref={containerRef}>{renderAd()}</div>;
};

const RecircNativeAdReactWithErrorBoundary = (props: Props) => {
    return (
        <ErrorBoundary fallback={null}>
            <RecircNativeAdReact {...props} />
        </ErrorBoundary>
    );
};
export default RecircNativeAdReactWithErrorBoundary;
